/**
 * @copyright Copyright 2025-2025 Epic Systems Corporation
 * @file Modal that shows a confirmation message with checkbox requirement
 * @module Epic.AppOrchard.Core.ConfirmationModalWithCheckbox
 */

import { Box, Checkbox, VStack } from "@chakra-ui/react";
import React, { FC, memo, useState } from "react";
import { ConfirmationModal, ConfirmationModalProps } from "./ConfirmationModal";

interface IProps extends ConfirmationModalProps {
	checkboxLabel: React.ReactNode;
}

export const ConfirmationModalWithCheckbox: FC<IProps> = memo((props: IProps) => {
	const { message, checkboxLabel, acceptButtonProps, ...rest } = props;

	const [isChecked, setIsChecked] = useState(false);

	const enhancedMessage = (
		<VStack align="stretch" spacing={4}>
			<Box>{message}</Box>
			<Checkbox isChecked={isChecked} onChange={(e) => setIsChecked(e.target.checked)}>
				{checkboxLabel}
			</Checkbox>
		</VStack>
	);

	const enhancedAcceptButtonProps = {
		...acceptButtonProps,
		isDisabled: !isChecked || acceptButtonProps?.isDisabled,
	};

	return (
		<ConfirmationModal
			{...rest}
			message={enhancedMessage}
			acceptButtonProps={enhancedAcceptButtonProps}
		/>
	);
});
